import { useState, type CSSProperties, type KeyboardEvent, type MouseEvent } from 'react';
import { Button, Check, Image } from '../../../modules/design-system';
import { CountdownBadge } from './CountdownBadge';
import type { IframePoolOrderItem } from '../../../modules/api';
import { formatPrice, loadOperatorCurrency } from '../../../modules/currency';
import { PHOTO_BG_STYLE } from '../photoBgStyle';

export interface OpenedItemCardProps {
  item: IframePoolOrderItem;
  /**
   * Tap anywhere on the card (except the Buyback pill) → toggle this
   * item's membership in the My Packs selection set. Earlier this opened
   * the detail modal — selection is the active behaviour now.
   */
  onClick?: (item: IframePoolOrderItem) => void;
  /**
   * Tap the Buyback pill → instant sell at `bestActiveOffer.grossPrice`.
   * Pill renders as a real button only when this is wired and there is an
   * active offer with a positive price.
   */
  onSell?: (item: IframePoolOrderItem) => Promise<void> | void;
  /**
   * Whether this card is in the active selection set — drives the green
   * outline + checkmark badge. The card stays clickable in both states.
   */
  selected?: boolean;
}

/**
 * Two-layer glass background. Figma stacks a light vertical gradient on
 * top of a flat dark tint — the dark base is what makes the card read as
 * darker overall than the page underneath. Multiple `background-image`
 * layers paint top-to-bottom, so the gradient sits over the tint.
 */
const CARD_GLASS_BG =
  'linear-gradient(' +
  'var(--co-MyPacksItemCard-bgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-MyPacksItemCard-bgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-MyPacksItemCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-MyPacksItemCard-bgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-MyPacksItemCard-bgOpacity, 1) * 100%), transparent) 100%), ' +
  'linear-gradient(90deg, ' +
  'var(--co-MyPacksItemCard-darkTint, rgba(0,0,0,0.18)) 0%, ' +
  'var(--co-MyPacksItemCard-darkTint, rgba(0,0,0,0.18)) 100%)';

/**
 * Cards-tab grid cell — one per opened pool_item. Glass panel with the
 * card photo on a pedestal, a countdown chip in the corner and a
 * Buyback-Offer pill at the bottom. Click targets:
 *   • Buyback pill → instant `sellPoolItem` (no modal).
 *   • Anywhere else on the card → toggle selection (drives the green
 *     outline + checkmark badge; bulk Buyback in the toolbar acts on
 *     the resulting set).
 *
 * The outer element is a `<div role="button">` (not `<button>`) because
 * nested interactive `<button>`s are invalid HTML — the pill needs to be
 * its own button to capture clicks via `stopPropagation`.
 */
export function OpenedItemCard({ item, onClick, onSell, selected = false }: OpenedItemCardProps) {
  const buyback = item.bestActiveOffer?.grossPrice ?? null;
  const canSell = onSell && buyback !== null && buyback > 0 && !!item.poolOrderId;
  const buybackAmount =
    buyback !== null && buyback > 0 && loadOperatorCurrency()
      ? formatPrice(buyback, 2)
      : '';
  const hasBuyback = buybackAmount !== '';
  // Mobile (Figma 278:7388) collapses the label to "Buyback $XX.XX";
  // ≥600px keeps the original "Buyback Offer: $XX.XX" form.
  const buybackLabel = hasBuyback ? (
    <>
      <span>Buyback</span>
      <span className="hidden @[600px]:inline">{' '}Offer:</span>
      {' '}
      <span>{buybackAmount}</span>
    </>
  ) : null;

  const [selling, setSelling] = useState(false);
  const [sellError, setSellError] = useState<string | null>(null);

  const handleSell = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onSell || selling) return;
    setSelling(true);
    setSellError(null);
    try {
      await onSell(item);
    } catch (err) {
      setSellError(err instanceof Error ? err.message : 'Sell failed');
      setSelling(false);
    }
  };

  const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(item);
    }
  };

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick ? () => onClick(item) : undefined}
      onKeyDown={onClick ? handleCardKeyDown : undefined}
      data-co-component="MyPacksItemCard"
      data-selected={selected ? 'true' : undefined}
      aria-pressed={onClick ? selected : undefined}
      aria-label={onClick ? item.title : undefined}
      className="
        relative flex w-full flex-col items-center overflow-hidden text-left
        rounded-[20px] p-1 pb-3
        @[600px]:rounded-[32px] @[600px]:pb-4
        transition-[transform,border-color,box-shadow] active:scale-[0.99]
      "
      style={{
        backgroundImage: CARD_GLASS_BG,
        backdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-MyPacksItemCard-glassBlur, 27px))',
        borderStyle: 'solid',
        borderWidth: 'var(--co-MyPacksItemCard-borderWidth, 2px)',
        borderColor: selected
          ? 'var(--co-MyPacksItemCard-selectedBorderColor, #23D11D)'
          : 'var(--co-MyPacksItemCard-borderColor, rgba(171,174,179,0.24))',
        boxShadow: selected
          ? '0 0 0 1px var(--co-MyPacksItemCard-selectedBorderColor, #23D11D), 0 8px 24px rgba(35,209,29,0.18)'
          : undefined,
        fontFamily: 'var(--co-MyPacksItemCard-fontFamily), Inter, system-ui, sans-serif',
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      {selected && (
        <span
          aria-hidden
          className="
            absolute left-3 top-3 z-10 flex size-[24px] items-center justify-center
            rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.25)]
          "
          style={{
            background: 'var(--co-MyPacksItemCard-checkmarkBg, #23D11D)',
            color: 'var(--co-MyPacksItemCard-checkmarkColor, #FFFFFF)',
          }}
        >
          <Check size={16} strokeWidth={3} />
        </span>
      )}
      <div
        className="relative w-full overflow-hidden rounded-[14px] @[600px]:rounded-[28px]"
        style={PHOTO_BG_STYLE}
      >
        <div className="relative aspect-square w-full @[600px]:aspect-[5/6]">
          <Image
            src={item.imageUrl}
            alt={item.title}
            className="absolute inset-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-contain p-3 @[600px]:p-6"
            quality={95}
          />
        </div>
        <CountdownBadge
          autoSellAt={item.bestActiveOffer?.autoSellAt ?? null}
          offerCreatedAt={item.bestActiveOffer?.createdAt ?? null}
        />
      </div>

      {/* Wrapper enforces a hard clip (overflow-hidden) so nothing can
          leak under the ellipsis. The inner `<p>` carries the
          `-webkit-line-clamp` inline (Tailwind's `line-clamp-2` utility
          alone was letting a third line bleed through in this nested
          flex layout). */}
      <div
        className="
          w-full flex-1 px-3 pb-2 pt-3 text-center
          @[600px]:px-4 @[600px]:pb-3 @[600px]:pt-4
        "
        style={{ overflow: 'hidden' }}
      >
        <p
          className="text-[12px] @[600px]:text-[13px]"
          style={{
            color: 'var(--co-MyPacksItemCard-titleColor, #FFFFFF)',
            // Unitless leading so it scales with the responsive
            // text-size — a fixed `22px` collides at larger sizes.
            lineHeight: 1.4,
            letterSpacing: '-0.2px',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            maxHeight: 'calc(1.4em * 2)',
            overflow: 'hidden',
            wordBreak: 'break-word',
            margin: 0,
          }}
        >
          {item.title}
        </p>
      </div>

      {buybackLabel && (
        canSell ? (
          <div className="mx-2 mb-2 w-[calc(100%-16px)] @[600px]:mx-3 @[600px]:mb-3 @[600px]:w-[calc(100%-24px)]">
            <Button
              variant="glass-frosted"
              size="md"
              onClick={handleSell}
              loading={selling}
              fullWidth
              aria-label={`Sell for ${buybackAmount}`}
              style={{ '--co-Button-glass-frostedBorderWidth': 0 } as CSSProperties}
            >
              {buybackLabel}
            </Button>
          </div>
        ) : (
          <div
            aria-hidden
            className="
              mx-3 flex h-[45px] w-[calc(100%-24px)] items-center justify-center
              rounded-full
            "
            style={{
              backgroundImage: CARD_GLASS_BG,
              color: 'var(--co-MyPacksItemCard-buybackText, #FFFFFF)',
            }}
          >
            <span
              className="whitespace-nowrap font-semibold"
              style={{ fontSize: '13px', lineHeight: '22px', letterSpacing: '-0.3px' }}
            >
              {buybackLabel}
            </span>
          </div>
        )
      )}

      {sellError && (
        <p
          role="alert"
          className="mt-2 px-3 text-center text-[12px]"
          style={{ color: 'var(--ui-accent, #F87171)' }}
        >
          {sellError}
        </p>
      )}
    </div>
  );
}
